import { maxBy, minBy } from 'es-toolkit';
import React from 'react';
import { Button, FormControl, InputLabel, MenuItem, Paper, Select } from '@mui/material';

import { Component, HeelReinforcementType, isProfileTruss, Member } from '@paragon/contract-library';
import { PropertiesPanel } from '@paragon/layout-models';
import { DistanceField } from '@paragon/material';
import { calculateMemberDirectionedMidline, calculateMemberPolygon } from '@paragon/three-shared';

import { setProfileTrussHeelReinforcement } from '../../../../store/actions/editComponentActions';
import { getActiveComponentDesign } from '../../../../store/selectors/getActiveComponentDesignSelector';
import { propertiesShouldBeViewOnly } from '../../../../store/selectors/viewOnlyModeSelectors';
import { setTrussEditsPanel } from '../../../../store/slices/panelDataSlice';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { HeelHeightMacroInput } from '../../subpanels/properties/HeelHeightMacroInput';

interface HeelReinforcementSettings {
  leftHeelReinforcementDistance: number | null;
  rightHeelReinforcementDistance: number | null;
  leftHeelReinforcementType: HeelReinforcementType;
  rightHeelReinforcementType: HeelReinforcementType;
}

const membersTouch = (component: Component, member1: Member, member2: Member) => {
  const polygon1 = calculateMemberPolygon(member1, component);
  const polygon2 = calculateMemberPolygon(member2, component);

  if (!polygon1 || !polygon2) return false;

  return polygon1.edges.some((edge1) => polygon2.edges.some((edge2) => edge1.overlaps(edge2)));
};

const inferHeelReinforcements = (component: Component): HeelReinforcementSettings => {
  const settings: HeelReinforcementSettings = {
    leftHeelReinforcementType: 'None',
    rightHeelReinforcementType: 'None',
    leftHeelReinforcementDistance: null,
    rightHeelReinforcementDistance: null,
  };

  const { members } = component;
  const topChords = members.filter((member) => member.memberType === 'TopChord');
  const bottomChords = members.filter((member) => member.memberType === 'BottomChord');
  const heelMembers = members.filter((member) => member.memberType === 'Slider' || member.memberType === 'Wedge');
  if (bottomChords.length === 0 || bottomChords.length === 0) return settings;

  const leftTopChord = minBy(topChords, (chord) => Math.min(...chord.geometry.map((point) => point.x)))!;
  const rightTopChord = maxBy(topChords, (chord) => Math.max(...chord.geometry.map((point) => point.x)))!;
  const leftBottomChord = minBy(bottomChords, (chord) => Math.min(...chord.geometry.map((point) => point.x)))!;
  const rightBottomChord = maxBy(bottomChords, (chord) => Math.max(...chord.geometry.map((point) => point.x)))!;

  const leftHeel = heelMembers.find(
    (member) => membersTouch(component, member, leftTopChord) && membersTouch(component, member, leftBottomChord),
  );
  const rightHeel = heelMembers.find(
    (member) => membersTouch(component, member, rightTopChord) && membersTouch(component, member, rightBottomChord),
  );

  if (leftHeel) {
    const leftHeelMemberMidline = calculateMemberDirectionedMidline(leftHeel, component)?.toLine();
    const leftTopChordMidline = calculateMemberDirectionedMidline(leftTopChord, component)?.toLine();
    if (!leftHeelMemberMidline || !leftTopChordMidline) return settings;

    if (leftHeelMemberMidline.isParallelTo(leftTopChordMidline)) {
      settings.leftHeelReinforcementType = 'TopChordSlider';
    } else if (leftHeel.geometry.length === 3) {
      settings.leftHeelReinforcementType = 'Wedge';
    } else {
      settings.leftHeelReinforcementType = 'BottomChordSlider';
    }

    if (
      settings.leftHeelReinforcementType === 'BottomChordSlider' ||
      settings.leftHeelReinforcementType === 'TopChordSlider'
    ) {
      const extremeY =
        settings.leftHeelReinforcementType === 'BottomChordSlider'
          ? Math.min(...leftHeel.geometry.map((point) => point.y))!
          : Math.max(...leftHeel.geometry.map((point) => point.y))!;
      const extremePoints = leftHeel.geometry.filter((point) => point.y === extremeY);
      const distance = Math.max(...extremePoints.map((point) => point.x));
      settings.leftHeelReinforcementDistance = Number.isFinite(distance) ? distance : null;
    }
  }

  if (rightHeel) {
    const rightHeelMemberMidline = calculateMemberDirectionedMidline(rightHeel, component)?.toLine();
    const rightTopChordMidline = calculateMemberDirectionedMidline(rightTopChord, component)?.toLine();
    if (!rightHeelMemberMidline || !rightTopChordMidline) return settings;

    if (rightHeelMemberMidline.isParallelTo(rightTopChordMidline)) {
      settings.rightHeelReinforcementType = 'TopChordSlider';
    } else if (rightHeel.geometry.length === 3) {
      settings.rightHeelReinforcementType = 'Wedge';
    } else {
      settings.rightHeelReinforcementType = 'BottomChordSlider';
    }

    if (
      settings.rightHeelReinforcementType === 'BottomChordSlider' ||
      settings.rightHeelReinforcementType === 'TopChordSlider'
    ) {
      const extremeY =
        settings.rightHeelReinforcementType === 'BottomChordSlider'
          ? Math.min(...rightHeel.geometry.map((point) => point.y))!
          : Math.max(...rightHeel.geometry.map((point) => point.y))!;
      const extremePoints = rightHeel.geometry.filter((point) => point.y === extremeY);
      const distance = Math.min(...extremePoints.map((point) => point.x));
      settings.rightHeelReinforcementDistance = Number.isFinite(distance) ? component.span - distance : null;
    }
  }

  return settings;
};

export function HeelMacrosPanel() {
  const dispatch = useAppDispatch();
  const componentDesign = useAppSelector(getActiveComponentDesign);

  const isViewOnly = useAppSelector(propertiesShouldBeViewOnly);

  if (componentDesign == null || !isProfileTruss(componentDesign.component)) return null;

  const heelReinforcements = inferHeelReinforcements(componentDesign.component);

  const updateLeftHeelReinforcement = (settings: HeelReinforcementSettings) =>
    dispatch(
      setProfileTrussHeelReinforcement(
        componentDesign!.guid,
        settings.leftHeelReinforcementType,
        'Left',
        settings.leftHeelReinforcementDistance,
      ),
    );

  const updateRightHeelReinforcement = (settings: HeelReinforcementSettings) =>
    dispatch(
      setProfileTrussHeelReinforcement(
        componentDesign!.guid,
        settings.rightHeelReinforcementType,
        'Right',
        settings.rightHeelReinforcementDistance,
      ),
    );

  return (
    <PropertiesPanel
      title="Adjust Heels"
      sx={{
        paddingX: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        '&>*': { margin: 0 },
      }}
    >
      <HeelHeightMacroInput side="Left" disabled={isViewOnly} componentDesign={componentDesign} />
      <FormControl variant="outlined" size="small">
        <InputLabel>Left Heel Reinforcement</InputLabel>
        <Select
          value={heelReinforcements.leftHeelReinforcementType}
          onChange={(event) =>
            updateLeftHeelReinforcement({
              ...heelReinforcements,
              leftHeelReinforcementType: event.target.value as HeelReinforcementType,
            })
          }
          disabled={isViewOnly}
          label="Left Heel Reinforcement"
        >
          <MenuItem value="None">None</MenuItem>
          <MenuItem value="Wedge">Wedge</MenuItem>
          <MenuItem value="TopChordSlider">Top Chord Slider</MenuItem>
          <MenuItem value="BottomChordSlider">Bottom Chord Slider</MenuItem>
        </Select>
      </FormControl>
      <DistanceField
        label="Distance"
        value={heelReinforcements.leftHeelReinforcementDistance ?? 0}
        disabled={
          isViewOnly ||
          heelReinforcements.leftHeelReinforcementType === 'None' ||
          heelReinforcements.leftHeelReinforcementType === 'Wedge'
        }
        onSubmit={(value) =>
          updateLeftHeelReinforcement({ ...heelReinforcements, leftHeelReinforcementDistance: value })
        }
      />
      <HeelHeightMacroInput side="Right" disabled={isViewOnly} componentDesign={componentDesign} />
      <FormControl variant="outlined" size="small">
        <InputLabel>Right Heel Reinforcement</InputLabel>
        <Select
          value={heelReinforcements.rightHeelReinforcementType}
          onChange={(event) =>
            updateRightHeelReinforcement({
              ...heelReinforcements,
              rightHeelReinforcementType: event.target.value as HeelReinforcementType,
            })
          }
          disabled={isViewOnly}
          label="Right Heel Reinforcement"
        >
          <MenuItem value="None">None</MenuItem>
          <MenuItem value="Wedge">Wedge</MenuItem>
          <MenuItem value="TopChordSlider">Top Chord Slider</MenuItem>
          <MenuItem value="BottomChordSlider">Bottom Chord Slider</MenuItem>
        </Select>
      </FormControl>
      <DistanceField
        label="Distance"
        value={heelReinforcements.rightHeelReinforcementDistance ?? 0}
        disabled={
          isViewOnly ||
          heelReinforcements.rightHeelReinforcementType === 'None' ||
          heelReinforcements.rightHeelReinforcementType === 'Wedge'
        }
        onSubmit={(value) =>
          updateRightHeelReinforcement({ ...heelReinforcements, rightHeelReinforcementDistance: value })
        }
      />
      <Paper elevation={1} sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', p: 1, borderRadius: 0 }}>
        <Button
          variant="outlined"
          onClick={() => {
            dispatch(setTrussEditsPanel(null));
          }}
        >
          Close
        </Button>
      </Paper>
    </PropertiesPanel>
  );
}
